import { css } from 'goober';
import { dlg } from '../helpers/dlg';
import { Component, formatDate, toDate } from '../helpers/helper';
import { service } from '../service';
import { state } from '../state';

const c_filters = css`
	display: flex;
	gap: 20px;
	align-items: center;
	margin-bottom: 10px;
	input,
	button,
	select {
		height: auto;
		width: unset;
		font-size: 12px;
		padding: 6px;
	}
`;

const c_info_modal = css`
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.45);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9000;
	padding: 1rem;

	.info-box {
		background: #fff;
		color: #1a1a2e;
		border-radius: 10px;
		box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
		width: 100%;
		max-width: 560px;
		max-height: 90vh;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}
	.info-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 1rem 1.25rem 0.75rem;
		border-bottom: 1px solid #e2e8f0;
		h3 { margin: 0; font-size: 1.1rem; color: #2375b7; }
		button {
			background: none;
			border: none;
			font-size: 1.4rem;
			line-height: 1;
			cursor: pointer;
			color: #666;
			padding: 0;
			width: auto;
			height: auto;
		}
	}
	.info-body {
		overflow-y: auto;
		padding: 1rem 1.25rem 1.25rem;
	}
	.info-section {
		margin-bottom: 1rem;
		h4 {
			margin: 0 0 0.4rem;
			font-size: 0.75rem;
			text-transform: uppercase;
			letter-spacing: 0.06em;
			color: #7a8499;
		}
	}
	.info-kv {
		display: grid;
		grid-template-columns: 120px 1fr;
		gap: 0.25rem 0.75rem;
		font-size: 0.875rem;
		.key { color: #6b7280; }
		.val { word-break: break-all; }
	}
	.info-list {
		list-style: none;
		margin: 0;
		padding: 0;
		font-size: 0.875rem;
		li {
			padding: 0.2rem 0;
			border-bottom: 1px solid #f1f5f9;
			&:last-child { border: none; }
		}
		.sub { color: #6b7280; font-size: 0.8rem; margin-left: 0.5rem; }
	}
`;

const MetricInfoModal = Component((p: { m: IMetric; onClose: () => void }) => {
	const { m } = p;
	const info = m.info || ({} as ILabMetrics);
	const apps = info.apps || [];
	const channels = [...new Set(info.channels || [])];
	const participants = info.participants || [];

	return () => (
		<div class={c_info_modal} onClick={p.onClose}>
			<div class="info-box" onClick={e => e.stopPropagation()}>
				<div class="info-header">
					<h3>Session Info</h3>
					<button onClick={p.onClose}>&times;</button>
				</div>
				<div class="info-body">
					{/* Overview */}
					<div class="info-section">
						<h4>Overview</h4>
						<div class="info-kv">
							<span class="key">Date</span>
							<span class="val">{formatDate(m.date)}</span>
							<span class="key">User</span>
							<span class="val">{m.user || '—'}</span>
							<span class="key">Duration</span>
							<span class="val">{m.seconds}s ({Math.round(m.seconds / 60)}m)</span>
							<span class="key">Location</span>
							<span class="val" style={{ wordBreak: 'break-all' }}>{m.location || '—'}</span>
							{m.referer && <span class="key">Referer</span>}
							{m.referer && <span class="val">{m.referer}</span>}
						</div>
					</div>

					{/* Geo */}
					{m.geo && (
						<div class="info-section">
							<h4>Location</h4>
							<div class="info-kv">
								<span class="key">City</span>
								<span class="val">{m.geo.city || '—'}</span>
								<span class="key">Region</span>
								<span class="val">{m.geo.region || '—'}</span>
								<span class="key">Country</span>
								<span class="val">{m.geo.country || '—'}</span>
							</div>
						</div>
					)}

					{/* Participants */}
					{participants.length > 0 && (
						<div class="info-section">
							<h4>Participants ({participants.length})</h4>
							<ul class="info-list">
								{participants.map(p => (
									<li key={p.id}>
										{p.name}
										<span class="sub">{p.id}</span>
									</li>
								))}
							</ul>
						</div>
					)}

					{/* Channels */}
					{channels.length > 0 && (
						<div class="info-section">
							<h4>Channels ({channels.length})</h4>
							<ul class="info-list">
								{channels.map(c => <li key={c}>{c}</li>)}
							</ul>
						</div>
					)}

					{/* Apps */}
					{apps.length > 0 && (
						<div class="info-section">
							<h4>Apps Used ({apps.length})</h4>
							<ul class="info-list">
								{apps.map((a, i) => (
									<li key={i}>
										{a.name}
										<span class="sub">{a.seconds}s — {a.startDate as string}</span>
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
			</div>
		</div>
	);
});

interface IUserGroup {
	user: string;
	list: IMetric[];
}

const GroupByUser = Component((p: { groups: IUserGroup[] }) => {
	const el = Vue.ref(null);
	return () => (
		<div class="dlg-wrapper" onClick={e => dlg.close(el)} ref={el}>
			<div class="dlg" style={{ background: 'white', maxHeight: '100%', overflow: 'auto' }}>
				{p.groups.map(g => (
					<a
						onClick={e => {
							dlg.close(el, g);
						}}
					>
						<div style={{ width: 200, overflow: 'hidden' }}>{g.user}</div>
						{g.list.length}
					</a>
				))}
			</div>
		</div>
	);
});

export const Metrics = Component(() => {
	const l = Vue.reactive({ metrics: [] as IMetric[], days: 7, filter: [] as IMetric[], infoMetric: null as IMetric | null });

	function getData() {
		service.getMetrics(l.days).then(r => {
			l.metrics = r;
			l.filter = l.metrics;
		});
	}

	function showInfo(m: IMetric) {
		l.infoMetric = m;
	}

	async function groupByUsers() {
		let user_map = new Map<string, IUserGroup>();

		for (let m of l.metrics) {
			if (!m.user) continue;
			let g = user_map.get(m.user);
			if (!g) {
				g = { user: m.user, list: [] };
				user_map.set(m.user, g);
			}
			g.list.push(m);
		}

		let groups = [...user_map.values()];
		groups.sort((a, b) => b.list.length - a.list.length);
		const g: IUserGroup = await dlg.open(GroupByUser, { groups });
		if (g) l.filter = g.list;
	}

	async function groupByDate() {
		let user_map = new Map<string, IUserGroup>();

		for (let m of l.metrics) {
			if (!m.user) continue;
			let d = (toDate(m.date) ?? new Date()).toDateString();
			let g = user_map.get(d);
			if (!g) {
				g = { user: d, list: [] };
				user_map.set(d, g);
			}
			g.list.push(m);
		}

		let groups = [...user_map.values()];
		const g: IUserGroup = await dlg.open(GroupByUser, { groups });
		if (g) l.filter = g.list;
	}

	async function groupByChannel() {
		let user_map = new Map<string, IUserGroup>();

		for (let m of l.metrics) {
			if (!m.info.channels) continue;
			for (let c of m.info?.channels) {
				let g = user_map.get(c);
				if (!g) {
					g = { user: c, list: [] };
					user_map.set(c, g);
				}
				g.list.push(m);
			}
		}

		let groups = [...user_map.values()];
		groups.sort((a, b) => b.list.length - a.list.length);
		const g: IUserGroup = await dlg.open(GroupByUser, { groups });
		if (g) l.filter = g.list;
	}

	getData();

	return () => (
		<div class="store-page page-light" id="metrics" onClick={e => e.stopPropagation()}>
			{l.infoMetric && (
				<MetricInfoModal m={l.infoMetric} onClose={() => (l.infoMetric = null)} />
			)}
			<div class={c_filters}>
				<select
					style={{ width: 'unset' }}
					value={l.days}
					onChange={e => {
						l.days = parseInt(e.target.value);
						getData();
					}}
				>
					<option value={7}>Last 7 days</option>
					<option value={30}>Last 30 days</option>
					<option value={90}>Last 90 days</option>
					<option value={365}>Last 365 days</option>
				</select>

				<button onClick={e => (l.filter = l.metrics)}>Show All</button>
				<button
					onClick={async () => {
						if (!confirm('Delete all metrics? This cannot be undone.')) return;
						await service.clearMetrics();
						l.metrics = [];
						l.filter = [];
					}}
				>
					Reset
				</button>
				<button onClick={groupByUsers}>Group By User</button>
				<button onClick={groupByDate}>Group By Date</button>
				<button onClick={groupByChannel}>Group By Channel</button>
			</div>
			<table>
				<thead>
					<tr>
						<th>date</th>
						<th>location</th>
						<th>user</th>
						<th>ad</th>
						<th>geo</th>
						<th>seconds</th>
						<th>channels</th>
						<th>referer</th>
						<th>info</th>
					</tr>
				</thead>
				<tbody>
					{l.filter.map(m => (
						<tr key={m.id}>
							<td>{formatDate(m.date)}</td>
							<td style={{ maxWidth: 300, wordBreak: 'break-all' }}>{m.location}</td>
							<td>{m.user?.slice(0, 20)}</td>
							<td>{m.ad}</td>
							<td>
								{m.geo && (
									<div>
										{m.geo.city}, {m.geo.region}
									</div>
								)}
							</td>
							<td>{m.seconds}</td>
							<td>
								{[...new Set(m.info?.channels)].map(c => (
									<div>{c.slice(0, 20)}</div>
								))}
							</td>
							<td>{m.referer}</td>
							<td>
								<a onClick={e => showInfo(m)}>View Info</a>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
});
